import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import { addUsersToDepartment } from "../../../../services/departments";

const AssignUsersToGroupContext = createContext();

const initialState = {
  assigning: false,
  error: null,
};

export const AssignUsersToGroupProvider = ({ children }) => {
  const [state, setState] = useState(initialState);
  const assignRequestIdRef = useRef(0);

  const updateState = useCallback((updates) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const assignUsers = useCallback(
    async (userUuids, departmentUuid) => {
      if (!userUuids || userUuids.length === 0) {
        const error = new Error("At least one user UUID is required");
        updateState({ error });
        throw error;
      }

      if (!departmentUuid) {
        const error = new Error("department_uuid is required");
        updateState({ error });
        throw error;
      }

      const requestId = ++assignRequestIdRef.current;

      updateState({ assigning: true, error: null });

      try {
        const response = await addUsersToDepartment({
          department_uuid: departmentUuid,
          user_uuids: userUuids,
        });

        if (requestId !== assignRequestIdRef.current) {
          updateState({ assigning: false });
          return;
        }

        const statusCode = response?.status_code || response?.statusCode;
        const isValidResponse = statusCode >= 200 && statusCode < 300;

        if (!isValidResponse) {
          const errorMessage =
            response?.msg_code?.[0]?.description ||
            response?.msgCode?.[0]?.description ||
            "Invalid response status code";
          throw new Error(errorMessage);
        }

        updateState({
          assigning: false,
          error: null,
        });

        return response;
      } catch (error) {
        if (requestId !== assignRequestIdRef.current) {
          updateState({ assigning: false });
          return;
        }

        updateState({
          assigning: false,
          error,
        });
        throw error;
      }
    },
    [updateState]
  );

  const value = {
    assigning: state.assigning,
    error: state.error,
    assignUsers,
  };

  return (
    <AssignUsersToGroupContext.Provider value={value}>
      {children}
    </AssignUsersToGroupContext.Provider>
  );
};

export const useAssignUsersToGroup = () => {
  const context = useContext(AssignUsersToGroupContext);
  if (!context) {
    throw new Error(
      "useAssignUsersToGroup must be used within AssignUsersToGroupProvider"
    );
  }
  return context;
};
